import { getUniquePersonIdValues } from 'features/globalFilters/personIdUtils';
import { PRIOR_COMPARISON_DISABLED_DATE } from 'features/globalFilters/globalFiltersDefaults';

const TABLEAU_FILTER_FIELDS = {
  currentPeriodFrom: 'current_start',
  currentPeriodTo: 'current_end',
  dateBasis: 'date_range',
  divisionLabels: 'division',
  personId: 'person_id',
  planLabels: 'plan',
  priorPeriodFrom: 'prior_start',
  priorPeriodTo: 'prior_end',
  tbView: 'tb_view'
};

const TABLEAU_RUNTIME_TARGETS = {
  parameter: 'parameter',
  filter: 'filter'
};

const TABLEAU_DATE_RANGE_VALUES = {
  1: '1',
  2: '2',
  'month paid': '1',
  'month incurred': '2',
  paid: '1',
  incurred: '2'
};

const TABLEAU_DATE_RANGE_NA_PARAMETER = 'date_range_na';

const DATE_BASIS_DISPLAY_LABELS = {
  1: 'Paid',
  2: 'Incurred',
  'month paid': 'Paid',
  'month incurred': 'Incurred',
  paid: 'Paid',
  incurred: 'Incurred'
};

const TABLEAU_DATE_INPUT_REGEX = /^(0[1-9]|1[0-2])\/(\d{4})$/;
const UNSPECIFIED_OPTION_LABEL = 'Unspecified';

// Maximum combined length (in characters) of all variable-length filter values
// that will be embedded in the Tableau viz URL via <viz-filter>. The Tableau
// embed library serializes <viz-filter> elements into the GET request URL, so
// we have to share a single budget across every list filter (divisions, plans,
// person ids) rather than checking each one in isolation. Anything that does
// not fit is applied asynchronously after the viz loads via the runtime
// filter plan.
const TABLEAU_VIZ_FILTER_MAX_TOTAL_LENGTH = 2000;

const normalizeStringValue = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeStringValue(entry))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[;,\n]+/)
      .map((entry) => normalizeStringValue(entry))
      .filter(Boolean);
  }

  return [];
};

const containsUnspecified = (values) => values.includes(UNSPECIFIED_OPTION_LABEL);

const expandUnspecifiedForTableau = (values) => [
  ...new Set(values.flatMap((value) =>
    value === UNSPECIFIED_OPTION_LABEL
      ? ['', UNSPECIFIED_OPTION_LABEL]
      : [value]
  ))
];

const containsUrlUnsafeDelimiter = (values = []) =>
  values.some((value) => normalizeStringValue(value).includes(','));

const getDateRangeValue = (value) => {
  const normalizedValue = normalizeStringValue(String(value ?? '')).toLowerCase();

  return TABLEAU_DATE_RANGE_VALUES[normalizedValue] || normalizeStringValue(String(value ?? ''));
};

const getOppositeDateRangeValue = (value) => {
  const normalizedValue = getDateRangeValue(value);

  if (normalizedValue === TABLEAU_DATE_RANGE_VALUES.paid) {
    return TABLEAU_DATE_RANGE_VALUES.incurred;
  }

  if (normalizedValue === TABLEAU_DATE_RANGE_VALUES.incurred) {
    return TABLEAU_DATE_RANGE_VALUES.paid;
  }

  return '';
};

const getTableauParameterCurrentValue = (parameter) => {
  const currentValue = parameter?.currentValue;

  if (currentValue && typeof currentValue === 'object') {
    return [
      currentValue.value,
      currentValue.nativeValue,
      currentValue.formattedValue,
      currentValue._value,
      currentValue._nativeValue,
      currentValue._formattedValue
    ].find((candidate) => candidate !== null && candidate !== undefined && candidate !== '');
  }

  return currentValue;
};

const normalizeTableauParameterName = (value) =>
  normalizeStringValue(String(value ?? '')).toLowerCase().replace(/[^a-z0-9]/g, '');

const getDateBasisNotAppliedValueForTableauParameters = (value, parameters = []) => {
  const dateRangeParameterName = normalizeTableauParameterName(TABLEAU_FILTER_FIELDS.dateBasis);
  const dateRangeNaParameterName = normalizeTableauParameterName(TABLEAU_DATE_RANGE_NA_PARAMETER);
  const hasDateRangeParameter = parameters.some(
    (parameter) => normalizeTableauParameterName(parameter?.name) === dateRangeParameterName
  );

  if (hasDateRangeParameter) {
    return null;
  }

  const dateRangeNaParameter = parameters.find(
    (parameter) => normalizeTableauParameterName(parameter?.name) === dateRangeNaParameterName
  );

  const staticDateBasisValue = getDateRangeValue(getTableauParameterCurrentValue(dateRangeNaParameter));

  return getOppositeDateRangeValue(staticDateBasisValue) || value;
};

const getRuntimeDateRangeValue = (value) => {
  const mappedValue = getDateRangeValue(value);
  const parsedValue = Number.parseInt(String(mappedValue), 10);

  return Number.isNaN(parsedValue) ? mappedValue : parsedValue;
};

const getDateBasisNotAppliedLabel = (value) => {
  const normalizedValue = normalizeStringValue(String(value ?? '')).toLowerCase();
  const displayLabel = DATE_BASIS_DISPLAY_LABELS[normalizedValue];

  return displayLabel ? `Date Basis: ${displayLabel}` : 'Date Basis';
};

const formatTableauDateValue = (value) => {
  const normalizedValue = normalizeStringValue(String(value ?? ''));
  const match = normalizedValue.match(TABLEAU_DATE_INPUT_REGEX);

  if (!match) {
    return normalizedValue;
  }

  const [, monthValue, yearValue] = match;
  return `${yearValue}-${monthValue}-01`;
};

const getTableauPersonIdValues = (appliedFilters = {}) =>
  getUniquePersonIdValues(appliedFilters.personIdValue);

const buildTableauVizFilters = (appliedFilters = {}) => {
  const filters = [];
  const candidateListFilters = [];

  let remainingBudget = TABLEAU_VIZ_FILTER_MAX_TOTAL_LENGTH;

  const serializeValue = (value) => {
    if (Array.isArray(value)) {
      return value.length ? value.join(',') : '';
    }
    return normalizeStringValue(String(value ?? ''));
  };

  const pushSerializedFilter = (field, serialized) => {
    if (!serialized) {
      return false;
    }
    const encodedLength = encodeURIComponent(serialized).length;
    if (encodedLength > remainingBudget) {
      return false;
    }
    filters.push({ field, value: serialized });
    remainingBudget -= encodedLength;
    return true;
  };

  const tryAddVizFilter = (field, value) => (
    pushSerializedFilter(field, serializeValue(value))
  );

  tryAddVizFilter(TABLEAU_FILTER_FIELDS.tbView, 'false');
  tryAddVizFilter(TABLEAU_FILTER_FIELDS.dateBasis, getDateRangeValue(appliedFilters.dateBasis));
  tryAddVizFilter(
    TABLEAU_FILTER_FIELDS.currentPeriodFrom,
    formatTableauDateValue(appliedFilters.currentPeriodFrom)
  );
  tryAddVizFilter(
    TABLEAU_FILTER_FIELDS.currentPeriodTo,
    formatTableauDateValue(appliedFilters.currentPeriodTo)
  );
  const priorPeriodFrom = appliedFilters.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : appliedFilters.priorPeriodFrom;
  const priorPeriodTo = appliedFilters.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : appliedFilters.priorPeriodTo;
  tryAddVizFilter(
    TABLEAU_FILTER_FIELDS.priorPeriodFrom,
    formatTableauDateValue(priorPeriodFrom)
  );
  tryAddVizFilter(
    TABLEAU_FILTER_FIELDS.priorPeriodTo,
    formatTableauDateValue(priorPeriodTo)
  );

  const divisionValues = normalizeMultiValue(appliedFilters.divisionLabels);
  const planValues = normalizeMultiValue(appliedFilters.planLabels);
  const shouldApplyDivisionFilter = !appliedFilters.isAllDivisionsSelected || appliedFilters.divisionsConstrainedByPlan === true;
  const shouldApplyPlanFilter = !appliedFilters.isAllPlansSelected || appliedFilters.plansConstrainedByDivision === true;

  // For variable-length list filters, serialize each candidate exactly once
  // (potentially-large arrays are joined a single time) and reuse that
  // serialized string for both the budget check and the final filters entry.
  if (shouldApplyDivisionFilter && !containsUnspecified(divisionValues) && !containsUrlUnsafeDelimiter(divisionValues)) {
    const serialized = serializeValue(divisionValues);
    if (serialized) {
      candidateListFilters.push({ field: TABLEAU_FILTER_FIELDS.divisionLabels, serialized });
    }
  }

  if (shouldApplyPlanFilter && !containsUnspecified(planValues) && !containsUrlUnsafeDelimiter(planValues)) {
    const serialized = serializeValue(planValues);
    if (serialized) {
      candidateListFilters.push({ field: TABLEAU_FILTER_FIELDS.planLabels, serialized });
    }
  }

  const personIdSerialized = serializeValue(getTableauPersonIdValues(appliedFilters));
  const personIdCandidate = personIdSerialized
    ? { field: TABLEAU_FILTER_FIELDS.personId, serialized: personIdSerialized }
    : null;

  // Pack list filters in declared order (division → plan → person_id).
  // Person ID is last so that, when the URL budget is tight, it falls through
  // to the runtime filter plan and is applied asynchronously after load.
  // Whatever does not fit at this point is applied asynchronously via the
  // runtime filter plan.
  candidateListFilters.forEach((candidate) => {
    pushSerializedFilter(candidate.field, candidate.serialized);
  });

  // Person ID is last-priority for the URL: when there is contention for the
  // URL budget, divisions and plans are placed first and person_id falls
  // through to be applied asynchronously by the runtime filter plan after the
  // viz loads. When there is room left, it rides in the URL instead — the
  // viz host (`Report`) skips the redundant runtime re-application on initial
  // load for any field that was placed in the URL.
  if (personIdCandidate) {
    pushSerializedFilter(personIdCandidate.field, personIdCandidate.serialized);
  }

  return filters;
};


const buildTableauRuntimeFilterPlan = (appliedFilters = {}) => {
  const normalizedDivisionValues = normalizeMultiValue(appliedFilters.divisionLabels);
  const normalizedPlanValues = normalizeMultiValue(appliedFilters.planLabels);
  const shouldApplyDivisionFilter = !appliedFilters.isAllDivisionsSelected || appliedFilters.divisionsConstrainedByPlan === true;
  const shouldApplyPlanFilter = !appliedFilters.isAllPlansSelected || appliedFilters.plansConstrainedByDivision === true;
  const divisionValues = shouldApplyDivisionFilter
    ? expandUnspecifiedForTableau(normalizedDivisionValues)
    : [];
  const planValues = shouldApplyPlanFilter
    ? expandUnspecifiedForTableau(normalizedPlanValues)
    : [];

  const priorPeriodFrom = appliedFilters.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : appliedFilters.priorPeriodFrom;
  const priorPeriodTo = appliedFilters.enablePriorComparison === false
    ? PRIOR_COMPARISON_DISABLED_DATE
    : appliedFilters.priorPeriodTo;

  const runtimePlan = [
    {
      field: TABLEAU_FILTER_FIELDS.tbView,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: false
    },
    {
      field: TABLEAU_FILTER_FIELDS.dateBasis,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: getRuntimeDateRangeValue(appliedFilters.dateBasis)
    },
    {
      field: TABLEAU_FILTER_FIELDS.currentPeriodFrom,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: formatTableauDateValue(appliedFilters.currentPeriodFrom)
    },
    {
      field: TABLEAU_FILTER_FIELDS.currentPeriodTo,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: formatTableauDateValue(appliedFilters.currentPeriodTo)
    },
    {
      field: TABLEAU_FILTER_FIELDS.priorPeriodFrom,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: formatTableauDateValue(priorPeriodFrom)
    },
    {
      field: TABLEAU_FILTER_FIELDS.priorPeriodTo,
      target: TABLEAU_RUNTIME_TARGETS.parameter,
      value: formatTableauDateValue(priorPeriodTo)
    },
    {
      field: TABLEAU_FILTER_FIELDS.divisionLabels,
      target: TABLEAU_RUNTIME_TARGETS.filter,
      values: divisionValues,
      clearWhenEmpty: true
    },
    {
      field: TABLEAU_FILTER_FIELDS.planLabels,
      target: TABLEAU_RUNTIME_TARGETS.filter,
      values: planValues,
      clearWhenEmpty: true
    },
    {
      field: TABLEAU_FILTER_FIELDS.personId,
      target: TABLEAU_RUNTIME_TARGETS.filter,
      values: getTableauPersonIdValues(appliedFilters),
      clearWhenEmpty: true
    }
  ];

  return runtimePlan;
};

const getTableauRuntimeFilterKey = (appliedFilters = {}) =>
  JSON.stringify(buildTableauRuntimeFilterPlan(appliedFilters));

// Returns the set of Tableau field names that were embedded in the viz URL
// via <viz-filter>. Used by the viz host to skip the redundant runtime
// re-application of those same filters on initial load.
const getTableauVizFilterFields = (vizFilters = []) =>
  new Set(vizFilters.map((entry) => entry.field).filter(Boolean));

const getTableauFilterRenderKey = (appliedFilters = {}) =>
  JSON.stringify({
    personIds: getTableauPersonIdValues(appliedFilters),
    vizFilters: buildTableauVizFilters(appliedFilters)
  });

export {
  TABLEAU_FILTER_FIELDS,
  TABLEAU_RUNTIME_TARGETS,
  buildTableauRuntimeFilterPlan,
  getDateBasisNotAppliedLabel,
  getDateBasisNotAppliedValueForTableauParameters,
  getTableauRuntimeFilterKey,
  buildTableauVizFilters,
  getTableauFilterRenderKey,
  getTableauPersonIdValues,
  getTableauVizFilterFields
};